import React from "react";

import { AlertTriangle, Check, MonitorPlay, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { OptisignsWriteConfirm } from "@/lib/agent/payload-parser";

interface OptisignsWriteConfirmHITLProps {
  summary: string;
  confirm: OptisignsWriteConfirm;
  /** True once the user confirmed (card collapses to a confirmed state). */
  isConfirmed: boolean;
  /** True once the user cancelled (card collapses to a dismissed state). */
  isCancelled: boolean;
  /** True if a newer message superseded this card (agent moved on). */
  isStale?: boolean;
  /** Re-invoke the agent with the pending op + args + confirmed:true. */
  onConfirm: () => void;
  /** Dismiss — no OptiSigns mutation runs. */
  onCancel: () => void;
}

/**
 * Inline chat HITL for a pending OptiSigns write (`optisigns_write_confirm`).
 *
 * The only path to a live write: on Confirm the agent is re-invoked with the
 * exact `op` + `args` from the payload plus `confirmed: true` (handled by the
 * parent's `onAction`), mirroring how every other HITL action button resumes a
 * turn. Cancel is a pure dismiss — no mutation is executed.
 */
export function OptisignsWriteConfirmHITL({
  summary,
  confirm,
  isConfirmed,
  isCancelled,
  isStale,
  onConfirm,
  onCancel,
}: OptisignsWriteConfirmHITLProps): React.ReactElement {
  // Generic destructive-action confirm (delete / remove / unpair / cancel) —
  // driven entirely by summary/title/detail, no "asset → screen" line.
  const isGeneric = confirm.confirmKind === "generic";

  if (isConfirmed) {
    return (
      <div className="w-fit rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2 text-sm">
          {/* green-500, matching the done chip in ClaudeResponseCard + cache-ttl-hitl */}
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          </span>
          <span className="font-medium text-foreground">
            {isGeneric ? "Confirmed — working…" : `Assigning to ${confirm.screenName}…`}
          </span>
        </div>
      </div>
    );
  }

  if (isCancelled || isStale) {
    return (
      <div className="w-fit rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <X className="h-3.5 w-3.5" />
          <span>{isGeneric ? "Cancelled" : "Assignment cancelled"}</span>
        </div>
      </div>
    );
  }

  if (isGeneric) {
    return (
      <div className="w-full max-w-[360px] space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium text-foreground">
            {confirm.title || "Confirm OptiSigns action"}
          </span>
        </div>

        {summary && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {summary}
          </p>
        )}

        {confirm.detail && (
          <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm text-foreground">
            {confirm.detail}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="h-8 px-3 text-xs"
          >
            Cancel
          </Button>
          <Button
            variant="contrast"
            size="sm"
            onClick={onConfirm}
            className={cn("h-8 px-3 text-xs")}
          >
            <Check className="mr-1.5 h-3.5 w-3.5" />
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[360px] space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <MonitorPlay className="h-4 w-4 text-foreground" />
        <span className="text-sm font-medium text-foreground">
          Confirm OptiSigns assignment
        </span>
      </div>

      {summary && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {summary}
        </p>
      )}

      <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm text-foreground">
        Assign <span className="font-semibold">{confirm.assetName}</span> → screen{" "}
        <span className="font-semibold">{confirm.screenName}</span>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="h-8 px-3 text-xs"
        >
          Cancel
        </Button>
        <Button
          variant="contrast"
          size="sm"
          onClick={onConfirm}
          className={cn("h-8 px-3 text-xs")}
        >
          <Check className="mr-1.5 h-3.5 w-3.5" />
          Confirm
        </Button>
      </div>
    </div>
  );
}
